<template>
    <div class="dashboard" :class="{ 'dashboard--board': isBoard }">
        <dashboard-header :open="openSidebar" @toggleSidebar="toggleSidebar" @logout="logout" @sidebar-button="toggleSidebar"/>
        <dashboard-sidebar :open="openSidebar" @toggleSidebar="toggleSidebar" @closeSidebar="closeSidebar" @logout="logout" class="dashboard__sidebar"/>
        <div
            class="dashboard__content"
            :class="{
                'dashboard__content--open': openSidebar,
                'dashboard__content--workspace': isWorkspace,
            }"
        >
            <div v-if="filterAccount && viewInfo" class="active-user-container">
                <div v-if="activeAccount && viewInfo">
                    <active-user :account="activeAccount" />
                </div>
            </div>
            <router-view v-if="hasPermission" :key="routerViewKey" />
            <error-state :error="error" v-else />
        </div>
        <auto-logout />
    </div>
</template>

<script>
import AutoLogout from '@/components/AutoLogout.vue';
import DashboardHeader from '@/components/layouts/dashboard/Header.vue';
import DashboardSidebar from '@/components/layouts/dashboard/Sidebar.vue';
import { api } from '@/services';
import ErrorState from '@/components/ui/ErrorState.vue';
import ActiveUser from '@/layouts/ActiveUser.vue';
import { mapState, mapActions } from 'vuex';
import { isAccountFilterablePath, isAccountsInfoRoute, isAccountsScopedRoute } from '@/utils/global-filters.util';
import { clearReturnPath } from '@/utils/return-path.util';

export default {
    components: {
        AutoLogout,
        DashboardHeader,
        DashboardSidebar,
        ErrorState,
        ActiveUser,
    },
    data() {
        return {
            openSidebar: false,
            windowWidth: 0,
            hasPermission: true,
            accountReady: false,
            error: {},
            routerViewKey: this.$route.fullPath,
            trackedAccountId: null,
            filterAccount: false,
            activeAccount: [],
            viewInfo: false,
        };
    },
    computed: {
        ...mapState(['globalFilters']),
        isBoard() {
            return this.$route.path === '/charts/salespeople' && this.$route.query.board === '1';
        },
        isWorkspace() {
            return this.$route.meta?.workspace === true;
        },
    },
    watch: {
        globalFilters: {
            handler(newVal) {
                if (newVal.account !== 0) {
                    this.activeAccount = newVal.account;
                    this.filterAccount = true;
                    this.checkInfo();
                    const nextAccountId = newVal.account.id;
                    const accountChanged = this.consumeAccountChange(nextAccountId);
                    if (isAccountsInfoRoute(this.$route.name)) {
                        this.openSelectedAccount(nextAccountId, accountChanged);
                    } else if (isAccountFilterablePath(this.$route.path)) {
                        this.routerViewKey = Date.now();
                    } else {
                        this.$router.replace({
                            name: 'accounts-info',
                            query: { id: nextAccountId },
                        });
                    }
                } else {
                    this.trackedAccountId = null;
                    this.activeAccount = [];
                    this.filterAccount = false;
                    if (this.$route.name != "accounts-info") {
                        this.routerViewKey = Date.now();
                    }
                }
            },
            deep: true,
        },
        $route(to) {
            this.clearAccountFilterIfViewIgnoresIt(to.path);
            this.checkInfo();
            window.addEventListener("resize", this.getWindowWidth);
            if (this.accountReady) {
                this.checkPermissions();
            }
        },
        '$route.query.id': function (newId, oldId) {
            if (newId !== oldId) {
            this.routerViewKey = Date.now();
            }
        }
    },
    created() {
        this.trackedAccountId = this.currentFilteredAccountId();
    },
    async mounted() {
        try {
            await this.$router.isReady();
            await this.$store.dispatch('accounts/getAccount');
        } catch {
            return;
        }
        this.accountReady = true;
        this.checkInfo();
        this.checkGlobalFilters();
        this.getWindowWidth();
        this.checkPermissions();
        window.addEventListener("resize", this.getWindowWidth);
        try {
            await this.$store.dispatch('adminNotifications/init');
        } catch (e) {
            console.error(e);
        }
    },
    beforeUnmount() {
        this.$store.dispatch('adminNotifications/teardown');
    },
    methods: {
        ...mapActions({
            setAccount: 'globalFilters/setAccount',
            clearAccountFilter: 'globalFilters/clearAccountFilter',
        }),
        toggleSidebar() {
            this.openSidebar = !this.openSidebar;
        },
        closeSidebar() {
            this.openSidebar = false;
        },
        getWindowWidth() {
            const width = window.innerWidth;
            const isDesktop = width > 992;
            if (!this.windowWidth || (this.windowWidth > 992) !== isDesktop) {
                this.openSidebar = isDesktop;
            }
            this.windowWidth = width;
        },
        async logout() {
            try {
                this.$store.dispatch('adminNotifications/teardown');
                await this.clearAccountFilter();
                await api.logout();
                this._deleteCookie("_tid");
                clearReturnPath();
                this.$router.push("/login");
            } catch (error) {
                console.error(error);
            }
        },
        checkPermissions() {
            const required_scopes = this.$route.meta?.scopes;
            if (!Array.isArray(required_scopes) || required_scopes.length === 0) {
                this.hasPermission = true;
                this.error = {};
                return;
            }
            const allowed = this.CAN(required_scopes, this.$route.meta.profile, this.$route.meta.bypassPositions);
            this.hasPermission = allowed;
            if (!allowed) {
                this.error = {
                    status: 403,
                    error: 'Forbidden',
                    message: 'You do not have permission to access this page.',
                };
                return;
            }
            this.error = {};
        },
        async checkGlobalFilters() {
            try {
                this.clearAccountFilterIfViewIgnoresIt();
                if (this.globalFilters.account !== 0) {
                    this.activeAccount = this.globalFilters.account;
                    this.filterAccount = true;
                    this.checkInfo()
                } else {
                    this.activeAccount = [];
                    this.routerViewKey = this.$route.fullPath;
                    this.filterAccount = false;
                }
            } catch (error) {
                this.$toast.error(this.getErrorMessage(error));
            }
        },
        checkInfo() {
            if (isAccountsScopedRoute(this.$route.name)) {
                this.viewInfo = false;
            } else {
                this.viewInfo = true;
            }
        },
        clearAccountFilterIfViewIgnoresIt(path = this.$route.path) {
            if (this.globalFilters.account !== 0 && !isAccountFilterablePath(path)) {
                this.clearAccountFilter();
            }
        },
        clearAccount() {
            this.clearAccountFilter();
        },
        currentFilteredAccountId() {
            const account = this.globalFilters?.account;
            if (!account || account === 0) {
                return null;
            }
            return account.id ?? null;
        },
        consumeAccountChange(nextAccountId) {
            const changed = String(nextAccountId ?? '') !== String(this.trackedAccountId ?? '');
            this.trackedAccountId = nextAccountId ?? null;
            return changed;
        },
        openSelectedAccount(accountId, accountChanged) {
            const routeId = this.$route.query?.id != null ? String(this.$route.query.id) : null;
            const sameAccount = String(accountId ?? '') === String(routeId ?? '');
            if (accountChanged) {
                const alreadyOnDetails = this.$route.name === 'accounts-info'
                    && sameAccount
                    && !this.$route.query?.company_id;
                if (alreadyOnDetails) {
                    return;
                }
                this.$router.replace({
                    name: 'accounts-info',
                    query: { id: accountId },
                });
                return;
            }
            if (!sameAccount) {
                this.$router.replace({
                    name: this.$route.name,
                    query: { ...this.$route.query, id: accountId },
                });
            }
        },
    },
};
</script>

<style lang="scss">
.dashboard {
    --sidebar-collapsed: 56px;
    --sidebar-open: 250px;
    max-width: 100%;
    overflow-x: hidden;
    width: 100%;

    &__content {
        float: right;
        margin-top: 56px;
        min-height: calc(100vh - 56px);
        min-width: 0;
        max-width: 100%;
        width: calc(100% - var(--sidebar-collapsed));
        box-sizing: border-box;
        transition: width 300ms;
        padding: 25px 16px;

        @media (max-width: 992px) {
            float: none;
            padding: 16px;
            width: 100%;
        }

        @media (max-width: 768px) {
            overflow-x: clip;
            padding: 12px;
            width: 100%;

            canvas,
            img,
            svg,
            video,
            iframe {
                max-width: 100%;
            }

            .row > [class*="col"] {
                flex: 0 0 100%;
                max-width: 100%;
                min-width: 0;
                width: 100%;
            }
        }

        &--open {
            width: calc(100% - var(--sidebar-open));

            @media (max-width: 992px) {
                width: 100%;
            }
        }

        &--workspace {
            overflow-x: auto;
            padding: 0;
        }
    }

    &--board {
        .header,
        .sidebar {
            display: none;
        }

        .dashboard__content {
            float: none;
            margin-top: 0;
            min-height: 100vh;
            padding: 24px;
            width: 100%;
        }
    }
}
.active-user-container {
    display: flex;
    align-items: center;

    .delete-button {
        margin-left: 10px;
        cursor: pointer;
    }
}
.chargebacks-list, .activitys-list {
    &__date-range-picker {
        align-items: center;
        background-color: $general-white;
        border: 1px solid $ecart-secondary-200;
        border-radius: 8px;
        display: flex;
        gap: 8px;
        height: 40px;
        padding: 4.5px 8px;
        width: 100%;

        .caret {
            margin-left: auto;

            &::before {
                color: $ecart-accent;
                content: '\f078';
                font-family: 'Font Awesome 5 Free', sans-serif;
                font-size: 16px;
                line-height: 1;
            }
        }
    }

    .vue-daterange-picker {
        width: 100%;
    }
}
</style>
